"use client";
import { FC, HTMLAttributes } from "react";
import clsx from "clsx";

import TButton from "@/components/atoms/buttons/TButton";

import CaseStudyCard from "@/components/cards/CaseStudyCard";
import ImageContentCard from "@/components/cards/ImageContentCard";
import EBooksCard from "@/components/cards/EBooksCard";
import UseCasesCard from "@/components/cards/UseCasesCard";
import { isEmpty } from "lodash";
import { getImageUrl } from "@/utils/media.helper";
import { IBannerSection } from "@/api/dtos/block-component.type";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { useInfiniteQuery } from "@tanstack/react-query";
import { resourcesInfiniteQuery } from "@/api/resource";

// ------------------------------------------------------------------------------------

export type IResourcesCategorySectionProps = HTMLAttributes<HTMLElement> & {
  param: string;
};

const ResourcesCategorySection: FC<IResourcesCategorySectionProps> = (
  props,
) => {
  const { param, className, ...other } = props;

  const { data, hasNextPage, fetchNextPage, isFetching, isLoading, isError } =
    useInfiniteQuery({
      ...resourcesInfiniteQuery.all({
        slug: `/${param}-details`,
        sort: "publishedAt:DESC",
        populate: "deep",
        pagination: { pageSize: 9 },
      }),
    });

  if (isEmpty(data?.pages[0].data) || isError) {
    return (
      <div className="container flex items-center justify-center py-5 lg:py-8 xl:py-[50px]">
        <h4 className="text-h6 font-medium text-gray-500 lg:text-h5 xl:text-h4">
          Data not found
        </h4>
      </div>
    );
  }

  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      {/* case study infinite data will showed up here on it's particular card */}
      {param === "case-study" && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-6 md:gap-y-8 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-[50px]">
          {/* in the case study card we gonna show heading from it's detail page's heading */}
          {data?.pages.map((page, pageIdx) =>
            page.data.map((d, dIdx) => (
              <CaseStudyCard
                key={`${pageIdx}-${dIdx}`}
                heading={
                  (
                    d.attributes.case_study_page?.filter(
                      (item) => item.__component === "blocks.banner-section",
                    )[0] as IBannerSection
                  )?.heading
                }
                image={{
                  src: getImageUrl(d.attributes.card_image),
                  alt: d.attributes.card_image.name ?? "",
                }}
                icon={d.attributes.card_icon?.name}
                button={{
                  id: `${param}-${d.attributes.slug}-navigate-button`,
                  link: `/resources/${param}/${d.attributes.slug}`,
                  target: "_self",
                  text: "View more..",
                  variant: "yellow",
                }}
              />
            )),
          )}
        </div>
      )}

      {/* use case infinite data will showed up here on it's particular card */}
      {param === "use-case" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-8">
          {/* in the use case card we gonna show heading and description from it's detail page's heading */}
          {data?.pages.map((page, pageIdx) =>
            page.data.map(({ attributes }, dIdx) => (
              <UseCasesCard
                key={`${pageIdx}-${dIdx}`}
                heading={
                  (
                    attributes.use_case_page?.filter(
                      (item) => item.__component === "blocks.banner-section",
                    )[0] as IBannerSection
                  )?.heading ?? undefined
                }
                image={{
                  src: getImageUrl(attributes.card_image),
                  alt: attributes.card_image.name ?? "",
                }}
                description={
                  <BlocksRenderer
                    content={
                      (
                        attributes.use_case_page?.filter(
                          (item) =>
                            item.__component === "blocks.banner-section" &&
                            (item as IBannerSection).description,
                        )[0] as IBannerSection
                      )?.description ?? []
                    }
                  />
                }
                button={{
                  id: `${param}-${attributes.slug}-navigate-button`,
                  link: `/resources/${param}/${attributes.slug}`,
                  target: "_self",
                  text: "View more..",
                  variant: "yellow",
                }}
              />
            )),
          )}
        </div>
      )}

      {/* sample report infinite data will showed up here on it's particular card */}
      {param === "sample-report" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-8">
          {/* in the sample report card we gonna show heading and description from it's detail page's heading */}
          {data?.pages.map((page, pageIdx) =>
            page.data.map(({ attributes }, dIdx) => (
              <UseCasesCard
                key={`${pageIdx}-${dIdx}`}
                heading={
                  (
                    attributes.sample_report_page?.filter(
                      (item) => item.__component === "blocks.banner-section",
                    )[0] as IBannerSection
                  )?.heading ?? undefined
                }
                image={{
                  src: getImageUrl(attributes.card_image),
                  alt: attributes.card_image.name ?? "",
                }}
                description={
                  <BlocksRenderer
                    content={
                      (
                        attributes.sample_report_page?.filter(
                          (item) =>
                            item.__component === "blocks.banner-section" &&
                            (item as IBannerSection).description,
                        )[0] as IBannerSection
                      )?.description ?? []
                    }
                  />
                }
                button={{
                  id: `${param}-${attributes.slug}-navigate-button`,
                  link: `/resources/${param}/${attributes.slug}`,
                  target: "_self",
                  text: "View more..",
                  variant: "yellow",
                }}
              />
            )),
          )}
        </div>
      )}

      {/* e book infinite data will showed up here on it's particular card */}
      {param === "e-book" && (
        <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-[50px]">
          {/* in the e book card we gonna show heading and description from it's detail page's heading */}
          {data?.pages.map((page, pageIdx) =>
            page.data.map((d, dIdx) => (
              <EBooksCard
                key={`${pageIdx}-${dIdx}`}
                heading={
                  (
                    d.attributes.e_book_page?.filter(
                      (item) => item.__component === "blocks.banner-section",
                    )[0] as IBannerSection
                  )?.heading ?? undefined
                }
                image={{
                  src: getImageUrl(d.attributes.card_image),
                  alt: d.attributes.card_image.name ?? "",
                }}
                description={
                  <BlocksRenderer
                    content={
                      (
                        d.attributes.e_book_page?.filter(
                          (item) =>
                            item.__component === "blocks.banner-section" &&
                            (item as IBannerSection).description,
                        )[0] as IBannerSection
                      )?.description ?? []
                    }
                  />
                }
                button={{
                  id: `${param}-${d.attributes.slug}-navigate-button`,
                  link: `/resources/${param}/${d.attributes.slug}`,
                  target: "_self",
                  text: "View more..",
                  variant: "yellow",
                }}
              />
            )),
          )}
        </div>
      )}

      {/* data sheet infinite data will showed up here on it's particular card */}
      {param === "data-sheet" && (
        <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-7">
          {data?.pages.map((page, pageIdx) =>
            page.data.map((d, dIdx) => (
              // in the data sheet card we gonna show heading and description from it's detail page's heading
              <ImageContentCard
                key={`${pageIdx}-${dIdx}`}
                heading={
                  (
                    d.attributes.data_sheet_page?.filter(
                      (item) => item.__component === "blocks.banner-section",
                    )[0] as IBannerSection
                  )?.heading
                }
                icon={d.attributes.card_icon?.name}
                description={
                  <BlocksRenderer
                    content={
                      (
                        d.attributes.data_sheet_page?.filter(
                          (item) =>
                            item.__component === "blocks.banner-section" &&
                            (item as IBannerSection).description,
                        )[0] as IBannerSection
                      )?.description ?? []
                    }
                  />
                }
                button={{
                  id: `${param}-${d.attributes.slug}-navigate-button`,
                  link: `/resources/${param}/${d.attributes.slug}`,
                  target: "_self",
                  text: "View more..",
                  variant: "yellow",
                }}
              />
            )),
          )}
        </div>
      )}

      {hasNextPage && (
        <div className="mt-8 text-center xl:mt-[50px]">
          <TButton
            id={`${param}-load-more-button`}
            variant="primary"
            onClick={() => fetchNextPage()}
            disabled={isFetching}
          >
            Load More
          </TButton>
        </div>
      )}
    </section>
  );
};

export default ResourcesCategorySection;
